import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { ClientService } from '../../client.service';
import { FeatureNames } from './operational-data.constants';

import { DcFeatures, FeatureDeprecated } from './operational-data.model';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class OperationalDataApi {
  constructor(
    private clientService: ClientService,
    private appInsights: AppInsightsMonitoringService
  ) {}

  /**
   * Returns list of all actions available on the service.
   * @param version
   */
  getEntryPoint(version?: string): Observable<any> {
    const ver = this.clientService.getVersion(version);
    const endpoint = `operational-data/${ver}/`;

    return this.clientService.get(endpoint);
  }

  /**
   * Returns list of all gateway features including all devices features
   * @param installationId
   * @param gatewaySerial
   */
  listAllGatewayFeatures(installationId: number, gatewaySerial: string): Observable<DcFeatures> {
    const endpoint = `iot/v2/features/installations/${installationId}/gateways/${gatewaySerial}/features`;

    this.appInsights.logEvent(APP_INSIGHTS_FEATURES.GET_GATEWAY_FEATURES, LogLevelEnum.TRACE);

    let httpParams = new HttpParams();
    httpParams = httpParams.append('includeDevicesFeatures', true);

    return this.clientService.get(endpoint, httpParams);
  }

  /**
   * Get single feature.
   * @param installationId
   * @param gatewaySerial
   * @param featureName
   */
  getGatewayFeature(
    installationId: number,
    gatewaySerial: string,
    featureName: string
  ): Observable<{ data: FeatureDeprecated }> {
    const endpoint = `iot/v2/features/installations/${installationId}/gateways/${gatewaySerial}/features/${featureName}`;

    if (featureName === FeatureNames.gatewayRemoteDiagnostics) {
      this.appInsights.logEvent(
        APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_GATEWAY_REMOTE_DIAGNOSTICS_FEATURE_ATTEMPT,
        LogLevelEnum.TRACE
      );
    }

    return this.clientService.get(endpoint);
  }

  /**
   * Perform given action (command) on gateway feature.
   * @param installationId
   * @param gatewaySerial
   * @param featureName
   * @param actionName
   * @param actionBody
   */
  performGatewayFeatureAction(
    installationId: number,
    gatewaySerial: string,
    featureName: string,
    actionName: string,
    actionBody: any
  ): Observable<{}> {
    // eslint-disable-next-line max-len
    const endpoint = `iot/v1/equipment/installations/${installationId}/gateways/${gatewaySerial}/features/${featureName}/${actionName}`;

    return this.clientService.post(endpoint, actionBody);
  }

  /**
   * Returns single device feature.
   * @param installationId
   * @param gatewaySerial
   * @param pairingId
   * @param featureName
   * @param reduceHypermedia
   */
  // TODO make it generic and apply return type getDeviceFeature<T,M> https://jira.viessmann-climatesolutions.com/browse/VIG-14297
  getDeviceFeature(
    installationId: number,
    gatewaySerial: string,
    pairingId: string,
    featureName: string
  ): Observable<{ data: FeatureDeprecated }> {
    // eslint-disable-next-line max-len
    const endpoint = `iot/v2/features/installations/${installationId}/gateways/${gatewaySerial}/devices/${pairingId}/features/${featureName}`;

    return this.clientService.get(endpoint);
  }
}
